'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';
import Link from 'next/link';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log to error tracking service in production
    if (process.env.NODE_ENV === 'production') {
      // Could send to Sentry, etc.
    }
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center bg-[#FDF6E3] p-6">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-4">
              Algo salió mal
            </h2>
            <p className="text-stone-600 font-serif mb-6">
              Ha ocurrido un error inesperado. Puedes intentar recargar la página
              o volver al inicio.
            </p>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => window.location.reload()}
                className="btn-primary"
              >
                Recargar
              </button>
              <Link 
                href="/" 
                className="text-stone-600 hover:text-stone-900 font-serif underline"
              >
                Ir al inicio
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
